/**
 * CollapsedRailView — the Closed area's default state: a 44px rail carrying a
 * count badge over a quarter-turned summary of what is closed (SE-6862, Figma
 * node 80:18284).
 *
 * The rotation carries a layout trap worth stating plainly. A rotated element
 * keeps its PRE-transform layout box, so the text still occupies a wide, short
 * box that the 44px rail would clip — the quarter turn is painted, not laid out.
 * Giving the inner box an explicit width equal to the outer box's height, from
 * the same sizing token, makes the two agree after the turn. That is why the
 * `h-48` / `w-48` pairing must stay in lockstep; changing one alone silently
 * crops the label.
 *
 * The whole rail is the expand target — at this width there is nothing else to
 * aim at, and a small dedicated control would be a worse touch target than the
 * rail itself.
 * @module features/player-kanban/components/organisms/ClosedArea/CollapsedRail/CollapsedRailView
 */

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for {@link CollapsedRailView}. */
export interface CollapsedRailViewProps {
  /** One-line summary of what is closed, e.g. "Closed · Signed 4 · Passed 8". */
  readonly railLabel: string;
  /** Total closed-card count, shown in the badge. */
  readonly totalCount: number;
  /** Expand the Closed area. */
  readonly onToggle: () => void;
}

/**
 * Renders the collapsed rail.
 * @param props - {@link CollapsedRailViewProps}
 * @param props.railLabel - One-line summary of what is closed
 * @param props.totalCount - Total closed-card count
 * @param props.onToggle - Expand handler
 * @returns The collapsed rail.
 */
const CollapsedRailView = ({
  railLabel,
  totalCount,
  onToggle,
}: CollapsedRailViewProps) => (
  <Pressable
    testID={TEST_ID_PLAYER_KANBAN.CLOSED_AREA_TOGGLE}
    onPress={onToggle}
    accessibilityRole="button"
    accessibilityLabel="Expand closed"
    className="web:hover:bg-surface-subtle w-full items-center gap-2 rounded-lg py-3"
  >
    <TonePill
      tone="neutral-base"
      label={String(totalCount)}
      textVariant="micro"
      className="shrink-0 px-2 py-micro"
    />
    <Box className="h-48 w-full items-center justify-center overflow-hidden">
      <Box className="w-48 rotate-90 items-center">
        <Text
          variant="micro"
          numberOfLines={1}
          className="text-center text-content-secondary"
        >
          {railLabel}
        </Text>
      </Box>
    </Box>
  </Pressable>
);

CollapsedRailView.displayName = "CollapsedRailView";

export default memo(CollapsedRailView);
